// 人工 IM 历史消息的顺序归整 —— 纯函数,便于单测。
// 背景:外部 vocechat history 与 WebSocket 实时推送两路汇入同一条 messages 列表,
// 渲染层按数组序输出、不排序;故在写入侧保证 IM 段严格按 mid 升序。

import type { UIMessage } from '../types';

/** IM 消息 id 形如 `im-<mid>`;非 IM id 返回 NaN */
export function imMid(id: string): number {
  return id.startsWith('im-') ? Number(id.slice(3)) : NaN;
}

const isIm = (m: UIMessage): boolean => m.id.startsWith('im-');

/**
 * 合并「已在列表中的 IM 消息」与「新到历史」,按 id 去重、按 mid 升序,
 * 非 IM 消息(AI / system)保持原相对位置并整体置于 IM 段之前。
 *
 * 用于初始 hydrate:消除「WebSocket 消息抢先 append、历史随后逐条 append 到其后」
 * 造成的乱序，并用 clientMsgId 替换尚未收到服务端回声的乐观访客消息。
 */
export function mergeImMessages(current: UIMessage[], incoming: UIMessage[]): UIMessage[] {
  const nonIm = current.filter((m) => !isIm(m));
  const byId = new Map<string, UIMessage>();
  const byClientMsgId = new Map<string, UIMessage>();
  const replacedOptimisticIds = new Set<string>();
  for (const m of current) if (isIm(m)) byId.set(m.id, m);
  for (const m of current) {
    if (!isIm(m) && m.clientMsgId) byClientMsgId.set(m.clientMsgId, m);
  }
  for (const m of incoming) {
    if (!isIm(m)) continue;
    const optimistic = m.clientMsgId ? byClientMsgId.get(m.clientMsgId) : undefined;
    if (optimistic) replacedOptimisticIds.add(optimistic.id);
    if (!byId.has(m.id)) byId.set(m.id, m);
  }
  const orderedIm = [...byId.values()].sort((a, b) => imMid(a.id) - imMid(b.id));
  return [...nonIm.filter((m) => !replacedOptimisticIds.has(m.id)), ...orderedIm];
}
